import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowDown,
  ArrowUp,
  Building2,
  Calculator,
  Check,
  Copy,
  FileText,
  FolderKanban,
  History,
  Loader2,
  Pencil,
  Plus,
  Send,
  Trash2,
  X,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { ReferenceIdBadge } from "@/components/ui/ReferenceIdBadge";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { ActivityHistoryDrawer } from "@/components/audit/ActivityHistoryDrawer";
import {
  useAddProposalItem,
  useAddProposalSection,
  useDeleteProposal,
  useDeleteProposalItem,
  useDeleteProposalSection,
  useDuplicateProposal,
  useProposal,
  useReorderProposalSections,
  useUpdateProposal,
  useUpdateProposalItem,
  useUpdateProposalSection,
  useUpdateProposalStatus,
} from "../hooks/useProposals";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import { formatCurrency } from "@/modules/expenses/components/CurrencyAmount";
import { ExternalCompanyPicker } from "@/components/shared/ExternalCompanyPicker";
import { ProposalStatusBadge } from "./ProposalListPage";
import type { ProposalItem, ProposalSection } from "../types";

const BORDER = "hsl(210,30%,85%)";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

type EditingItem = {
  id: string | null;
  sectionId: string;
  description: string;
  itemType: string;
  unitPrice: string;
  quantity: string;
  unit: string;
  isOptional: boolean;
  isIncluded: boolean;
};

function emptyItem(sectionId: string, itemType = "fixed"): EditingItem {
  return {
    id: null,
    sectionId,
    description: "",
    itemType,
    unitPrice: "",
    quantity: "1",
    unit: "",
    isOptional: false,
    isIncluded: false,
  };
}

export default function ProposalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: proposal, isLoading } = useProposal(id ?? "");
  const updateProposal = useUpdateProposal(id ?? "");
  const updateStatus = useUpdateProposalStatus();
  const deleteProposal = useDeleteProposal();
  const duplicateProposal = useDuplicateProposal();

  const addSection = useAddProposalSection(id ?? "");
  const updateSection = useUpdateProposalSection(id ?? "");
  const deleteSection = useDeleteProposalSection(id ?? "");
  const reorderSections = useReorderProposalSections(id ?? "");

  const addItem = useAddProposalItem(id ?? "");
  const updateItem = useUpdateProposalItem(id ?? "");
  const deleteItem = useDeleteProposalItem(id ?? "");

  const { data: sectionTypeItems = [] } = useCategoryItems(
    "proposal_section_type",
  );
  const { data: itemTypeItems = [] } = useCategoryItems("proposal_item_type");

  const [editingHeader, setEditingHeader] = useState(false);
  const [editName, setEditName] = useState("");
  const [editingItem, setEditingItem] = useState<EditingItem | null>(null);
  const [addingSection, setAddingSection] = useState(false);
  const [newSectionTitle, setNewSectionTitle] = useState("");
  const [newSectionType, setNewSectionType] = useState("custom");
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [sectionTitleDraft, setSectionTitleDraft] = useState("");
  const [editingDiscount, setEditingDiscount] = useState(false);
  const [discountDraft, setDiscountDraft] = useState("");
  const [editingTax, setEditingTax] = useState(false);
  const [taxDraft, setTaxDraft] = useState("");
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [editingTerms, setEditingTerms] = useState(false);
  const [termsDraft, setTermsDraft] = useState("");
  const [editingValidUntil, setEditingValidUntil] = useState(false);
  const [validUntilDraft, setValidUntilDraft] = useState("");
  const [editingClient, setEditingClient] = useState(false);
  const [clientDraft, setClientDraft] = useState("");
  const [rejectionOpen, setRejectionOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [activityOpen, setActivityOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState<ProposalSection | null>(null);
  const [itemToDelete, setItemToDelete] = useState<ProposalItem | null>(null);

  const sectionTypeLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of sectionTypeItems) map.set(s.value, s.label);
    return map;
  }, [sectionTypeItems]);

  const itemTypeLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const i of itemTypeItems) map.set(i.value, i.label);
    return map;
  }, [itemTypeItems]);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  }

  if (isLoading || !proposal) {
    return (
      <AppLayout
        breadcrumb={[
          { label: "PhazeX Hub", href: "/" },
          { label: "Finance" },
          { label: "Price Proposals", href: "/proposals" },
        ]}
      >
        <div className="py-12 text-center text-muted-foreground">
          {isLoading ? (
            <>
              <Loader2
                className="mx-auto h-6 w-6 animate-spin"
                strokeWidth={1.5}
              />
              <div className="mt-2 text-sm">Loading…</div>
            </>
          ) : (
            "Proposal not found"
          )}
        </div>
      </AppLayout>
    );
  }

  const currency = proposal.currency;

  async function saveHeaderName() {
    if (!editName.trim()) return;
    await updateProposal.mutateAsync({ name: editName.trim() });
    setEditingHeader(false);
    showToast("Name updated");
  }

  function handleDelete() {
    setDeleteOpen(true);
  }

  async function confirmDelete() {
    await deleteProposal.mutateAsync(proposal!.id);
    navigate("/proposals");
  }

  async function handleDuplicate() {
    const res = await duplicateProposal.mutateAsync(proposal!.id);
    showToast(`Duplicated as ${res.proposal.code}`);
    navigate(`/proposals/${res.proposal.id}`);
  }

  async function handleMarkSent() {
    await updateStatus.mutateAsync({
      id: proposal!.id,
      status: "sent",
    });
    showToast("Marked as sent");
  }

  async function handleMarkAccepted() {
    await updateStatus.mutateAsync({
      id: proposal!.id,
      status: "accepted",
    });
    showToast("Marked as accepted");
  }

  async function handleMarkRejected() {
    if (!rejectionReason.trim()) return;
    await updateStatus.mutateAsync({
      id: proposal!.id,
      status: "rejected",
      rejectionReason: rejectionReason.trim(),
    });
    setRejectionOpen(false);
    setRejectionReason("");
    showToast("Marked as rejected");
  }

  async function handleStatusSelect(status: string) {
    if (status === "rejected") {
      setRejectionOpen(true);
      return;
    }
    await updateStatus.mutateAsync({ id: proposal!.id, status });
    showToast(`Status → ${status}`);
  }

  async function handleAddSection() {
    if (!newSectionTitle.trim()) return;
    const sortOrder = (proposal!.sections[proposal!.sections.length - 1]?.sortOrder ?? -1) + 1;
    await addSection.mutateAsync({
      title: newSectionTitle.trim(),
      sectionType: newSectionType,
      sortOrder,
    });
    setNewSectionTitle("");
    setNewSectionType("custom");
    setAddingSection(false);
    showToast("Section added");
  }

  function handleDeleteSection(s: ProposalSection) {
    setSectionToDelete(s);
  }

  async function confirmDeleteSection() {
    if (!sectionToDelete) return;
    await deleteSection.mutateAsync(sectionToDelete.id);
    showToast("Section removed");
  }

  async function saveSectionTitle(s: ProposalSection) {
    if (!sectionTitleDraft.trim()) return;
    await updateSection.mutateAsync({
      sectionId: s.id,
      payload: { title: sectionTitleDraft.trim() },
    });
    setEditingSectionId(null);
    showToast("Section updated");
  }

  async function handleMoveSection(
    s: ProposalSection,
    direction: "up" | "down",
  ) {
    const idx = proposal!.sections.findIndex((x) => x.id === s.id);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= proposal!.sections.length) return;
    const other = proposal!.sections[swapIdx];
    await reorderSections.mutateAsync([
      { id: s.id, sortOrder: other.sortOrder },
      { id: other.id, sortOrder: s.sortOrder },
    ]);
  }

  function startNewItem(sectionId: string) {
    setEditingItem(
      emptyItem(sectionId, itemTypeItems[0]?.value ?? "fixed"),
    );
  }

  function startEditItem(item: ProposalItem) {
    setEditingItem({
      id: item.id,
      sectionId: item.sectionId,
      description: item.description,
      itemType: item.itemType,
      unitPrice: String(item.unitPrice),
      quantity: String(item.quantity),
      unit: item.unit ?? "",
      isOptional: item.isOptional,
      isIncluded: item.isIncluded,
    });
  }

  async function saveItem() {
    if (!editingItem) return;
    const unitPrice = Number(editingItem.unitPrice) || 0;
    const quantity = Number(editingItem.quantity) || 0;
    if (!editingItem.description.trim()) {
      showToast("Description required");
      return;
    }
    if (quantity <= 0) {
      showToast("Quantity must be > 0");
      return;
    }
    const payload = {
      description: editingItem.description.trim(),
      itemType: editingItem.itemType,
      unitPrice,
      quantity,
      unit: editingItem.unit.trim() || null,
      isOptional: editingItem.isOptional,
      isIncluded: editingItem.isIncluded,
    };
    if (editingItem.id) {
      await updateItem.mutateAsync({
        sectionId: editingItem.sectionId,
        itemId: editingItem.id,
        payload,
      });
      showToast("Item updated");
    } else {
      await addItem.mutateAsync({
        sectionId: editingItem.sectionId,
        payload,
      });
      showToast("Item added");
    }
    setEditingItem(null);
  }

  function handleDeleteItem(item: ProposalItem) {
    setItemToDelete(item);
  }

  async function confirmDeleteItem() {
    if (!itemToDelete) return;
    await deleteItem.mutateAsync({
      sectionId: itemToDelete.sectionId,
      itemId: itemToDelete.id,
    });
    showToast("Item removed");
  }

  async function saveDiscount() {
    const raw = discountDraft.trim();
    const pct = raw === "" ? null : Number(raw);
    if (pct !== null && (!Number.isFinite(pct) || pct < 0 || pct > 100)) {
      showToast("Invalid %");
      return;
    }
    await updateProposal.mutateAsync({ discountPct: pct });
    setEditingDiscount(false);
  }

  async function saveTax() {
    const raw = taxDraft.trim();
    const pct = raw === "" ? null : Number(raw);
    if (pct !== null && (!Number.isFinite(pct) || pct < 0 || pct > 100)) {
      showToast("Invalid %");
      return;
    }
    await updateProposal.mutateAsync({ taxPct: pct });
    setEditingTax(false);
  }

  async function toggleTaxIncluded() {
    await updateProposal.mutateAsync({ taxIncluded: !proposal!.taxIncluded });
  }

  async function saveValidUntil() {
    await updateProposal.mutateAsync({
      validUntil: validUntilDraft ? new Date(validUntilDraft).toISOString() : null,
    });
    setEditingValidUntil(false);
  }

  async function saveClient() {
    if (!clientDraft) {
      showToast("Client is required");
      return;
    }
    await updateProposal.mutateAsync({ clientCompanyId: clientDraft });
    setEditingClient(false);
    showToast("Client updated");
  }

  async function saveNotes() {
    await updateProposal.mutateAsync({ notes: notesDraft.trim() || null });
    setEditingNotes(false);
  }

  async function saveTerms() {
    await updateProposal.mutateAsync({ terms: termsDraft.trim() || null });
    setEditingTerms(false);
  }

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Finance" },
        { label: "Price Proposals", href: "/proposals" },
        { label: proposal.code },
      ]}
    >
      {toast && (
        <div
          className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg"
          style={{ borderColor: "hsl(142,55%,80%)" }}
        >
          {toast}
        </div>
      )}

      {rejectionOpen &&
        (() => {
          return (
            <div
              className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40"
              onClick={() => setRejectionOpen(false)}
            >
              <div
                className="w-[420px] rounded-xl border bg-white p-5 shadow-xl"
                style={{ borderColor: BORDER }}
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="mb-3 text-sm font-semibold">
                  Rejection Reason
                </h3>
                <textarea
                  rows={4}
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Why was this proposal rejected?"
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  autoFocus
                />
                <div className="mt-3 flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setRejectionOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleMarkRejected}
                    disabled={!rejectionReason.trim()}
                  >
                    Mark Rejected
                  </Button>
                </div>
              </div>
            </div>
          );
        })()}

      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/proposals")}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
          </button>
          <FileText
            className="h-[18px] w-[18px] text-foreground"
            strokeWidth={1.5}
          />
          <h1 className="text-xl font-semibold text-foreground">
            Price Proposal
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={proposal.status}
            onChange={(e) => handleStatusSelect(e.target.value)}
            className="h-8 w-36 text-xs"
          >
            <option value="draft">Draft</option>
            <option value="review">Review</option>
            <option value="sent">Sent</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
            <option value="expired">Expired</option>
          </Select>
          <button
            type="button"
            onClick={handleMarkSent}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
            title="Mark as sent"
          >
            <Send
              className="h-4 w-4 text-muted-foreground"
              strokeWidth={1.5}
            />
          </button>
          <button
            type="button"
            onClick={handleDuplicate}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
            title="Duplicate"
          >
            <Copy
              className="h-4 w-4 text-muted-foreground"
              strokeWidth={1.5}
            />
          </button>
          <button
            type="button"
            onClick={() => setActivityOpen(true)}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
            title="History"
          >
            <History
              className="h-4 w-4 text-muted-foreground"
              strokeWidth={1.5}
            />
          </button>
          <button
            type="button"
            onClick={handleDelete}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(0,70%,96%)]"
            title="Delete"
          >
            <Trash2
              className="h-4 w-4 text-[hsl(0,70%,45%)]"
              strokeWidth={1.5}
            />
          </button>
        </div>
      </div>

      {/* ROW 1 — Header */}
      <div
        className="mb-4 rounded-xl border bg-white"
        style={{ borderColor: BORDER, padding: "16px 20px" }}
      >
        {editingHeader ? (
          <div className="flex items-center gap-2">
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="text-[20px] font-bold"
              autoFocus
            />
            <button
              type="button"
              onClick={saveHeaderName}
              className="flex h-7 w-7 items-center justify-center rounded text-[hsl(142,72%,28%)] hover:bg-[hsl(142,55%,94%)]"
            >
              <Check className="h-4 w-4" strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={() => setEditingHeader(false)}
              className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted"
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        ) : (
          <div
            className="cursor-pointer text-[20px] font-bold text-foreground"
            onClick={() => {
              setEditName(proposal.name);
              setEditingHeader(true);
            }}
          >
            {proposal.name}
          </div>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[13px] text-muted-foreground">
          <ReferenceIdBadge id={proposal.code} />
          <ProposalStatusBadge status={proposal.status} />
          <span
            className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
            style={{
              background: "hsl(270,55%,92%)",
              color: "hsl(270,60%,35%)",
            }}
          >
            v{proposal.version}
          </span>
          {proposal.clientCompanyName && (
            <span className="inline-flex items-center gap-1">
              <Building2 className="h-3 w-3" strokeWidth={1.5} />
              <Link
                to={`/companies/${proposal.clientCompanyId}`}
                className="hover:text-[hsl(210,100%,35%)]"
              >
                {proposal.clientCompanyName}
              </Link>
            </span>
          )}
          {proposal.projectName && (
            <span className="inline-flex items-center gap-1">
              <FolderKanban className="h-3 w-3" strokeWidth={1.5} />
              <Link
                to={`/projects/${proposal.projectId}`}
                className="hover:text-[hsl(210,100%,35%)]"
              >
                {proposal.projectName}
              </Link>
            </span>
          )}
          {proposal.validUntil && (
            <span
              className={
                new Date(proposal.validUntil).getTime() < Date.now()
                  ? "font-semibold text-[hsl(0,70%,45%)]"
                  : ""
              }
            >
              Valid until {formatDate(proposal.validUntil)}
            </span>
          )}
        </div>
        {proposal.estimationId && (
          <div className="mt-2 flex items-center gap-1 text-[12px] text-muted-foreground">
            <Calculator className="h-3 w-3" strokeWidth={1.5} />
            Based on:{" "}
            <Link
              to={`/estimations/${proposal.estimationId}`}
              className="hover:text-[hsl(210,100%,35%)]"
            >
              <span className="font-mono">{proposal.estimationCode}</span>{" "}
              {proposal.estimationName}
            </Link>
          </div>
        )}
      </div>

      {proposal.status === "accepted" && proposal.acceptedAt && (
        <div
          className="mb-4 rounded-lg border px-4 py-2.5 text-sm"
          style={{
            borderColor: "hsl(142,55%,80%)",
            background: "hsl(142,55%,95%)",
            color: "hsl(142,72%,28%)",
          }}
        >
          ✓ Accepted on {formatDate(proposal.acceptedAt)}
        </div>
      )}
      {proposal.status === "rejected" && proposal.rejectedAt && (
        <div
          className="mb-4 rounded-lg border px-4 py-2.5 text-sm"
          style={{
            borderColor: "hsl(0,70%,80%)",
            background: "hsl(0,70%,96%)",
            color: "hsl(0,70%,35%)",
          }}
        >
          ✕ Rejected on {formatDate(proposal.rejectedAt)}
          {proposal.rejectionReason
            ? ` — ${proposal.rejectionReason}`
            : ""}
        </div>
      )}

      {/* ROW 2 — Sections */}
      <div className="mb-4 space-y-4">
        {proposal.sections.map((s, idx) => (
          <div
            key={s.id}
            className="overflow-hidden rounded-xl border bg-white"
            style={{ borderColor: BORDER }}
          >
            <div
              className="flex items-center justify-between border-b px-4 py-2.5"
              style={{
                borderColor: "hsl(210,30%,90%)",
                background: "hsl(210,30%,97%)",
              }}
            >
              <div className="flex items-center gap-2">
                <span
                  className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide"
                  style={{
                    background: "hsl(210,55%,92%)",
                    color: "hsl(210,100%,28%)",
                  }}
                >
                  {sectionTypeLabels.get(s.sectionType) ?? s.sectionType}
                </span>
                {editingSectionId === s.id ? (
                  <div className="flex items-center gap-1">
                    <Input
                      value={sectionTitleDraft}
                      onChange={(e) => setSectionTitleDraft(e.target.value)}
                      className="h-7 w-[240px] text-[13px]"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveSectionTitle(s);
                        if (e.key === "Escape") setEditingSectionId(null);
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => saveSectionTitle(s)}
                      className="flex h-6 w-6 items-center justify-center rounded text-[hsl(142,72%,28%)] hover:bg-[hsl(142,55%,94%)]"
                    >
                      <Check className="h-3 w-3" strokeWidth={1.5} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingSectionId(null)}
                      className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted"
                    >
                      <X className="h-3 w-3" strokeWidth={1.5} />
                    </button>
                  </div>
                ) : (
                  <span
                    className="cursor-pointer text-[14px] font-semibold text-foreground"
                    onClick={() => {
                      setSectionTitleDraft(s.title);
                      setEditingSectionId(s.id);
                    }}
                  >
                    {s.title}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => handleMoveSection(s, "up")}
                  disabled={idx === 0}
                  className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted disabled:opacity-40"
                  title="Move up"
                >
                  <ArrowUp className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
                <button
                  type="button"
                  onClick={() => handleMoveSection(s, "down")}
                  disabled={idx === proposal.sections.length - 1}
                  className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted disabled:opacity-40"
                  title="Move down"
                >
                  <ArrowDown className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
                <button
                  type="button"
                  onClick={() => startNewItem(s.id)}
                  className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted"
                  title="Add item"
                >
                  <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteSection(s)}
                  className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(0,70%,95%)] hover:text-[hsl(0,84%,50%)]"
                  title="Delete section"
                >
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
              </div>
            </div>

            <table
              className="w-full text-[13px]"
              style={{ borderCollapse: "collapse" }}
            >
              <thead className="bg-[hsl(210,30%,98%)] text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Description</th>
                  <th className="px-3 py-2" style={{ width: 110 }}>
                    Type
                  </th>
                  <th
                    className="px-3 py-2 text-right"
                    style={{ width: 120 }}
                  >
                    Unit Price
                  </th>
                  <th
                    className="px-3 py-2 text-right"
                    style={{ width: 70 }}
                  >
                    Qty
                  </th>
                  <th className="px-3 py-2" style={{ width: 70 }}>
                    Unit
                  </th>
                  <th
                    className="px-3 py-2 text-right"
                    style={{ width: 140 }}
                  >
                    Total
                  </th>
                  <th
                    className="px-3 py-2 text-right"
                    style={{ width: 80 }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {s.items.length === 0 &&
                  !(editingItem && editingItem.sectionId === s.id && !editingItem.id) && (
                    <tr>
                      <td
                        colSpan={7}
                        className="py-6 text-center text-[12px] text-muted-foreground"
                      >
                        No items yet.
                      </td>
                    </tr>
                  )}
                {s.items.map((item) =>
                  editingItem && editingItem.id === item.id ? (
                    <ProposalItemEditRow
                      key={item.id}
                      row={editingItem}
                      setRow={setEditingItem}
                      itemTypeItems={itemTypeItems}
                      currency={currency}
                      onSave={saveItem}
                      onCancel={() => setEditingItem(null)}
                      saving={updateItem.isPending}
                    />
                  ) : (
                    <ProposalItemRow
                      key={item.id}
                      item={item}
                      currency={currency}
                      itemTypeLabel={
                        itemTypeLabels.get(item.itemType) ?? item.itemType
                      }
                      onEdit={() => startEditItem(item)}
                      onDelete={() => handleDeleteItem(item)}
                    />
                  ),
                )}
                {editingItem &&
                  editingItem.sectionId === s.id &&
                  editingItem.id === null && (
                    <ProposalItemEditRow
                      row={editingItem}
                      setRow={setEditingItem}
                      itemTypeItems={itemTypeItems}
                      currency={currency}
                      onSave={saveItem}
                      onCancel={() => setEditingItem(null)}
                      saving={addItem.isPending}
                      isNew
                    />
                  )}
              </tbody>
            </table>

            <div
              className="flex items-center justify-between border-t px-4 py-2.5 text-[12px]"
              style={{
                borderColor: "hsl(210,30%,92%)",
                background: "hsl(210,30%,98%)",
              }}
            >
              <button
                type="button"
                onClick={() => startNewItem(s.id)}
                className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
              >
                <Plus className="h-3 w-3" strokeWidth={1.5} />
                Add Item
              </button>
              <div className="text-[13px] font-semibold text-foreground">
                Section Total: {formatCurrency(s.subtotal, currency)}
              </div>
            </div>
          </div>
        ))}

        {addingSection ? (
          <div
            className="rounded-xl border bg-white p-4"
            style={{ borderColor: BORDER }}
          >
            <div className="mb-2 text-sm font-semibold">New Section</div>
            <div className="grid grid-cols-[1fr_200px_auto_auto] gap-2">
              <Input
                value={newSectionTitle}
                onChange={(e) => setNewSectionTitle(e.target.value)}
                placeholder="Section title"
                autoFocus
              />
              <Select
                value={newSectionType}
                onChange={(e) => setNewSectionType(e.target.value)}
              >
                {sectionTypeItems.length === 0 ? (
                  <option value="custom">Custom</option>
                ) : (
                  sectionTypeItems.map((s) => (
                    <option key={s.id} value={s.value}>
                      {s.label}
                    </option>
                  ))
                )}
              </Select>
              <Button
                size="sm"
                onClick={handleAddSection}
                disabled={!newSectionTitle.trim()}
              >
                Add
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setAddingSection(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="outline"
            onClick={() => setAddingSection(true)}
            className="w-full"
          >
            <Plus className="mr-1 h-4 w-4" strokeWidth={1.5} />
            Add Section
          </Button>
        )}
      </div>

      {/* ROW 3 — Totals + Details */}
      <div
        className="grid gap-4"
        style={{ gridTemplateColumns: "60fr 40fr" }}
      >
        {/* LEFT — Totals */}
        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: BORDER, padding: "16px 20px" }}
        >
          <h3 className="mb-3 text-[13px] font-semibold text-foreground">
            Totals
          </h3>
          <div className="space-y-1 text-[13px]">
            {proposal.sections.map((s) => (
              <div
                key={s.id}
                className="flex items-center justify-between py-1"
              >
                <span className="text-muted-foreground">{s.title}</span>
                <span className="text-foreground">
                  {formatCurrency(s.subtotal, currency)}
                </span>
              </div>
            ))}
            <div
              className="mt-2 flex items-center justify-between border-t pt-2"
              style={{ borderColor: "hsl(210,30%,92%)" }}
            >
              <span className="text-muted-foreground">Subtotal</span>
              <span className="text-foreground">
                {formatCurrency(proposal.subtotal, currency)}
              </span>
            </div>
            <div className="flex items-center justify-between py-1">
              <span className="flex items-center gap-1 text-muted-foreground">
                Discount (
                {editingDiscount ? (
                  <>
                    <input
                      type="number"
                      value={discountDraft}
                      onChange={(e) => setDiscountDraft(e.target.value)}
                      className="w-[48px] rounded border border-border bg-white px-1 text-[12px]"
                      step="0.5"
                      min="0"
                      max="100"
                      autoFocus
                      onBlur={saveDiscount}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveDiscount();
                        if (e.key === "Escape") setEditingDiscount(false);
                      }}
                    />
                    %
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setDiscountDraft(
                        proposal.discountPct !== null
                          ? String(proposal.discountPct)
                          : "0",
                      );
                      setEditingDiscount(true);
                    }}
                    className="font-medium text-foreground underline decoration-dotted"
                  >
                    {proposal.discountPct ?? 0}%
                  </button>
                )}
                )
              </span>
              <span className="text-[hsl(0,70%,45%)]">
                -{formatCurrency(proposal.discountAmount, currency)}
              </span>
            </div>
            <div
              className="flex items-center justify-between border-t pt-2"
              style={{ borderColor: "hsl(210,30%,92%)" }}
            >
              <span className="text-muted-foreground">After Discount</span>
              <span className="text-foreground">
                {formatCurrency(
                  proposal.subtotal - proposal.discountAmount,
                  currency,
                )}
              </span>
            </div>
            <div className="flex items-center justify-between py-1">
              <span className="flex items-center gap-1 text-muted-foreground">
                VAT (
                {editingTax ? (
                  <>
                    <input
                      type="number"
                      value={taxDraft}
                      onChange={(e) => setTaxDraft(e.target.value)}
                      className="w-[48px] rounded border border-border bg-white px-1 text-[12px]"
                      step="0.5"
                      min="0"
                      max="100"
                      autoFocus
                      onBlur={saveTax}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveTax();
                        if (e.key === "Escape") setEditingTax(false);
                      }}
                    />
                    %
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setTaxDraft(
                        proposal.taxPct !== null
                          ? String(proposal.taxPct)
                          : "0",
                      );
                      setEditingTax(true);
                    }}
                    className="font-medium text-foreground underline decoration-dotted"
                  >
                    {proposal.taxPct ?? 0}%
                  </button>
                )}
                )
                <button
                  type="button"
                  onClick={toggleTaxIncluded}
                  className="ml-2 text-[11px] text-muted-foreground underline decoration-dotted"
                  title="Toggle tax included"
                >
                  {proposal.taxIncluded ? "included" : "added"}
                </button>
              </span>
              <span className="text-foreground">
                {proposal.taxIncluded
                  ? "—"
                  : `+${formatCurrency(proposal.taxAmount, currency)}`}
              </span>
            </div>
            <div
              className="flex items-center justify-between border-t-2 pt-2 text-[15px] font-bold"
              style={{ borderColor: "hsl(210,30%,85%)" }}
            >
              <span>TOTAL</span>
              <span>{formatCurrency(proposal.total, currency)}</span>
            </div>
          </div>

          {proposal.estimationId && proposal.estimationTotal !== null && (
            <div
              className="mt-6 rounded-lg border p-4 text-[12px]"
              style={{ borderColor: "hsl(210,30%,92%)" }}
            >
              <div className="mb-2 text-[11px] uppercase text-muted-foreground">
                Margin (vs linked estimation)
              </div>
              <div className="flex items-center justify-between py-1 text-foreground">
                <span>Estimated Cost</span>
                <span>
                  {formatCurrency(proposal.estimationTotal, currency)}
                </span>
              </div>
              <div className="flex items-center justify-between py-1 text-foreground">
                <span>Proposal Total</span>
                <span>{formatCurrency(proposal.total, currency)}</span>
              </div>
              {(() => {
                const margin = proposal.total - proposal.estimationTotal;
                const pct =
                  proposal.total > 0
                    ? (margin / proposal.total) * 100
                    : 0;
                const good = pct >= 0;
                return (
                  <>
                    <div
                      className="mt-2 flex items-center justify-between border-t pt-2 font-semibold"
                      style={{
                        borderColor: "hsl(210,30%,92%)",
                        color: good
                          ? "hsl(142,72%,28%)"
                          : "hsl(0,70%,45%)",
                      }}
                    >
                      <span>Gross Margin</span>
                      <span>
                        {formatCurrency(margin, currency)} (
                        {pct.toFixed(1)}%)
                      </span>
                    </div>
                    <div
                      className="mt-2 h-[6px] w-full rounded-full"
                      style={{ background: "hsl(210,30%,94%)" }}
                    >
                      <div
                        className="h-[6px] rounded-full"
                        style={{
                          width: `${Math.max(0, Math.min(100, pct))}%`,
                          background: good
                            ? "hsl(142,72%,48%)"
                            : "hsl(0,70%,55%)",
                        }}
                      />
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>

        {/* RIGHT — Details */}
        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: BORDER, padding: "16px 20px" }}
        >
          <h3 className="mb-3 text-[13px] font-semibold text-foreground">
            Proposal Details
          </h3>

          <div className="space-y-3 text-[12px]">
            <div className="flex items-center gap-2">
              {proposal.status === "draft" && (
                <Button size="sm" onClick={handleMarkSent}>
                  <Send className="mr-1 h-3 w-3" strokeWidth={1.5} />
                  Mark as Sent
                </Button>
              )}
              {proposal.status === "sent" && (
                <>
                  <Button
                    size="sm"
                    onClick={handleMarkAccepted}
                    className="bg-[hsl(142,72%,35%)] hover:bg-[hsl(142,72%,28%)]"
                  >
                    Mark Accepted
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setRejectionOpen(true)}
                  >
                    Mark Rejected
                  </Button>
                </>
              )}
            </div>

            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Valid Until
              </div>
              {editingValidUntil ? (
                <div className="mt-1 flex items-center gap-2">
                  <Input
                    type="date"
                    value={validUntilDraft}
                    onChange={(e) => setValidUntilDraft(e.target.value)}
                    className="h-8"
                  />
                  <button
                    type="button"
                    onClick={saveValidUntil}
                    className="flex h-7 w-7 items-center justify-center rounded text-[hsl(142,72%,28%)] hover:bg-[hsl(142,55%,94%)]"
                  >
                    <Check className="h-3 w-3" strokeWidth={1.5} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingValidUntil(false)}
                    className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted"
                  >
                    <X className="h-3 w-3" strokeWidth={1.5} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setValidUntilDraft(
                      proposal.validUntil
                        ? proposal.validUntil.slice(0, 10)
                        : "",
                    );
                    setEditingValidUntil(true);
                  }}
                  className="text-foreground underline decoration-dotted"
                >
                  {formatDate(proposal.validUntil)}
                </button>
              )}
            </div>

            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Currency
              </div>
              <div className="text-foreground">{proposal.currency}</div>
            </div>

            <div
              className="border-t pt-3"
              style={{ borderColor: "hsl(210,30%,92%)" }}
            >
              <div className="mb-1 flex items-center justify-between">
                <div className="text-[11px] uppercase text-muted-foreground">
                  Client
                </div>
                {!editingClient && (
                  <button
                    type="button"
                    onClick={() => {
                      setClientDraft(proposal.clientCompanyId ?? "");
                      setEditingClient(true);
                    }}
                    className="flex h-6 w-6 items-center justify-center rounded hover:bg-muted"
                    title="Change client"
                  >
                    <Pencil
                      className="h-3 w-3 text-muted-foreground"
                      strokeWidth={1.5}
                    />
                  </button>
                )}
              </div>
              {editingClient ? (
                <div className="space-y-2">
                  <ExternalCompanyPicker
                    id="prp-edit-client"
                    value={clientDraft}
                    onChange={setClientDraft}
                    systemCodes={["client", "customer"]}
                    placeholder="Select client company…"
                    required
                  />
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={saveClient}
                      disabled={!clientDraft || updateProposal.isPending}
                      className="flex h-7 w-7 items-center justify-center rounded text-[hsl(142,72%,28%)] hover:bg-[hsl(142,55%,94%)] disabled:opacity-40"
                    >
                      <Check className="h-3 w-3" strokeWidth={1.5} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingClient(false)}
                      className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted"
                    >
                      <X className="h-3 w-3" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              ) : proposal.clientCompanyId ? (
                <Link
                  to={`/companies/${proposal.clientCompanyId}`}
                  className="flex items-center gap-2 text-foreground hover:text-[hsl(210,100%,35%)]"
                >
                  <Building2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {proposal.clientCompanyName}
                </Link>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
              {proposal.projectId && (
                <Link
                  to={`/projects/${proposal.projectId}`}
                  className="mt-1 flex items-center gap-2 text-foreground hover:text-[hsl(210,100%,35%)]"
                >
                  <FolderKanban className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {proposal.projectName}
                </Link>
              )}
              {proposal.estimationId && (
                <Link
                  to={`/estimations/${proposal.estimationId}`}
                  className="mt-1 flex items-center gap-2 text-foreground hover:text-[hsl(210,100%,35%)]"
                >
                  <Calculator className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {proposal.estimationCode} — {proposal.estimationName}
                </Link>
              )}
            </div>

            <div
              className="border-t pt-3"
              style={{ borderColor: "hsl(210,30%,92%)" }}
            >
              <div className="mb-1 flex items-center justify-between">
                <div className="text-[11px] uppercase text-muted-foreground">
                  Notes
                </div>
                {!editingNotes && (
                  <button
                    type="button"
                    onClick={() => {
                      setNotesDraft(proposal.notes ?? "");
                      setEditingNotes(true);
                    }}
                    className="flex h-6 w-6 items-center justify-center rounded hover:bg-muted"
                  >
                    <Pencil
                      className="h-3 w-3 text-muted-foreground"
                      strokeWidth={1.5}
                    />
                  </button>
                )}
              </div>
              {editingNotes ? (
                <>
                  <textarea
                    rows={3}
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    onBlur={saveNotes}
                  />
                </>
              ) : (
                <div className="whitespace-pre-wrap text-foreground">
                  {proposal.notes ?? (
                    <span className="text-muted-foreground">
                      Click to add…
                    </span>
                  )}
                </div>
              )}
            </div>

            <div
              className="border-t pt-3"
              style={{ borderColor: "hsl(210,30%,92%)" }}
            >
              <div className="mb-1 flex items-center justify-between">
                <div className="text-[11px] uppercase text-muted-foreground">
                  Terms
                </div>
                {!editingTerms && (
                  <button
                    type="button"
                    onClick={() => {
                      setTermsDraft(proposal.terms ?? "");
                      setEditingTerms(true);
                    }}
                    className="flex h-6 w-6 items-center justify-center rounded hover:bg-muted"
                  >
                    <Pencil
                      className="h-3 w-3 text-muted-foreground"
                      strokeWidth={1.5}
                    />
                  </button>
                )}
              </div>
              {editingTerms ? (
                <textarea
                  rows={3}
                  value={termsDraft}
                  onChange={(e) => setTermsDraft(e.target.value)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  onBlur={saveTerms}
                />
              ) : (
                <div className="whitespace-pre-wrap text-foreground">
                  {proposal.terms ?? (
                    <span className="text-muted-foreground">
                      Click to add payment terms…
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ActivityHistoryDrawer
        isOpen={activityOpen}
        onClose={() => setActivityOpen(false)}
        entityType="price_proposal"
        entityId={proposal.id}
        entityLabel={proposal.name}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete proposal?"
        description={`Are you sure you want to delete '${proposal.name}'? It will be removed from your Proposals list.`}
        onConfirm={confirmDelete}
      />

      <ConfirmDialog
        open={sectionToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setSectionToDelete(null);
        }}
        title="Delete section?"
        description={
          sectionToDelete
            ? `Are you sure you want to delete the section '${sectionToDelete.title}' and its ${sectionToDelete.items.length} item${sectionToDelete.items.length === 1 ? "" : "s"}? They will be removed from the proposal.`
            : ""
        }
        onConfirm={confirmDeleteSection}
      />

      <ConfirmDialog
        open={itemToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setItemToDelete(null);
        }}
        title="Delete line item?"
        description="Are you sure you want to delete this line item? It will be removed from the proposal."
        onConfirm={confirmDeleteItem}
      />
    </AppLayout>
  );
}

function ProposalItemRow({
  item,
  currency,
  itemTypeLabel,
  onEdit,
  onDelete,
}: {
  item: ProposalItem;
  currency: string;
  itemTypeLabel: string;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const optional = item.isOptional;
  const included = item.isIncluded;
  return (
    <tr
      className="border-t transition-colors hover:bg-[hsl(210,50%,98%)]"
      style={{
        borderColor: "hsl(210,30%,92%)",
        background: optional ? "hsl(210,30%,98%)" : undefined,
      }}
    >
      <td className="px-3 py-2 text-foreground">
        <div className="flex items-center gap-2">
          <span>{item.description}</span>
          {optional && (
            <span
              className="rounded-full px-1.5 py-0.5 text-[10px] font-medium"
              style={{
                background: "hsl(38,92%,92%)",
                color: "hsl(38,92%,30%)",
              }}
            >
              Optional
            </span>
          )}
          {included && (
            <span
              className="rounded-full px-1.5 py-0.5 text-[10px] font-medium"
              style={{
                background: "hsl(142,55%,92%)",
                color: "hsl(142,72%,28%)",
              }}
            >
              Included
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-2 text-muted-foreground">{itemTypeLabel}</td>
      <td className="px-3 py-2 text-right text-foreground">
        {included ? (
          <span className="line-through text-muted-foreground">
            {formatCurrency(item.unitPrice, currency)}
          </span>
        ) : (
          formatCurrency(item.unitPrice, currency)
        )}
      </td>
      <td className="px-3 py-2 text-right text-foreground">{item.quantity}</td>
      <td className="px-3 py-2 text-muted-foreground">{item.unit ?? "—"}</td>
      <td className="px-3 py-2 text-right font-medium text-foreground">
        {included || optional
          ? formatCurrency(0, currency)
          : formatCurrency(item.totalPrice, currency)}
      </td>
      <td className="px-3 py-2 text-right">
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={onEdit}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted"
            title="Edit"
          >
            <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(0,70%,95%)] hover:text-[hsl(0,84%,50%)]"
            title="Delete"
          >
            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
        </div>
      </td>
    </tr>
  );
}

function ProposalItemEditRow({
  row,
  setRow,
  itemTypeItems,
  currency,
  onSave,
  onCancel,
  saving,
  isNew = false,
}: {
  row: EditingItem;
  setRow: (r: EditingItem) => void;
  itemTypeItems: { id: string; value: string; label: string }[];
  currency: string;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  isNew?: boolean;
}) {
  const unitPrice = Number(row.unitPrice) || 0;
  const quantity = Number(row.quantity) || 0;
  const total = row.isIncluded || row.isOptional ? 0 : Math.round(unitPrice * quantity * 100) / 100;
  return (
    <tr
      className="border-t"
      style={{
        background: "hsl(210,55%,96%)",
        borderColor: "hsl(210,55%,85%)",
      }}
    >
      <td className="px-3 py-2">
        <Input
          value={row.description}
          onChange={(e) => setRow({ ...row, description: e.target.value })}
          placeholder="Description"
          autoFocus={isNew}
          className="h-8 text-[12px]"
          onKeyDown={(e) => {
            if (e.key === "Enter") onSave();
            if (e.key === "Escape") onCancel();
          }}
        />
        <div className="mt-1 flex items-center gap-3 text-[11px] text-muted-foreground">
          <label className="flex items-center gap-1">
            <input
              type="checkbox"
              checked={row.isOptional}
              onChange={(e) =>
                setRow({
                  ...row,
                  isOptional: e.target.checked,
                  isIncluded: e.target.checked ? false : row.isIncluded,
                })
              }
            />
            Optional
          </label>
          <label className="flex items-center gap-1">
            <input
              type="checkbox"
              checked={row.isIncluded}
              onChange={(e) =>
                setRow({
                  ...row,
                  isIncluded: e.target.checked,
                  isOptional: e.target.checked ? false : row.isOptional,
                })
              }
            />
            Included free
          </label>
        </div>
      </td>
      <td className="px-3 py-2">
        <Select
          value={row.itemType}
          onChange={(e) => setRow({ ...row, itemType: e.target.value })}
          className="h-8 text-[12px]"
        >
          {itemTypeItems.length === 0 ? (
            <option value={row.itemType}>{row.itemType}</option>
          ) : (
            itemTypeItems.map((i) => (
              <option key={i.id} value={i.value}>
                {i.label}
              </option>
            ))
          )}
        </Select>
      </td>
      <td className="px-3 py-2">
        <Input
          type="number"
          value={row.unitPrice}
          step="0.01"
          min="0"
          onChange={(e) => setRow({ ...row, unitPrice: e.target.value })}
          className="h-8 text-right text-[12px]"
          onKeyDown={(e) => {
            if (e.key === "Enter") onSave();
            if (e.key === "Escape") onCancel();
          }}
        />
      </td>
      <td className="px-3 py-2">
        <Input
          type="number"
          value={row.quantity}
          step="0.5"
          min="0"
          onChange={(e) => setRow({ ...row, quantity: e.target.value })}
          className="h-8 text-right text-[12px]"
          onKeyDown={(e) => {
            if (e.key === "Enter") onSave();
            if (e.key === "Escape") onCancel();
          }}
        />
      </td>
      <td className="px-3 py-2">
        <Input
          value={row.unit}
          onChange={(e) => setRow({ ...row, unit: e.target.value })}
          placeholder="unit"
          className="h-8 text-[12px]"
        />
      </td>
      <td className="px-3 py-2 text-right font-medium text-foreground">
        {formatCurrency(total, currency)}
      </td>
      <td className="px-3 py-2 text-right">
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="flex h-7 w-7 items-center justify-center rounded text-[hsl(142,72%,28%)] hover:bg-[hsl(142,55%,94%)] disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
            ) : (
              <Check className="h-3.5 w-3.5" strokeWidth={1.5} />
            )}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted"
          >
            <X className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
        </div>
      </td>
    </tr>
  );
}
